import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { startOfWeek, addDays } from "@/lib/dateUtils";
import {
  WEEKLY_GOAL,
  getCurrentDailyStreak,
  getCurrentWeeklyStreak,
  buildStreakMaps,
} from "@/lib/streakUtils";

export interface CommandStats {
  dailyStreak: number;
  weeklyStreak: number;
  promptsThisWeek: number;
  totalPrompts: number;
  teamRank: number | null;
  teamMemberCount: number | null;
}

interface ScoredItem {
  created_at: string | null;
  score_total_1_100: number | null;
}

interface LeaderboardRow {
  user_id: string;
}

const EMPTY_STATS: CommandStats = {
  dailyStreak: 0,
  weeklyStreak: 0,
  promptsThisWeek: 0,
  totalPrompts: 0,
  teamRank: null,
  teamMemberCount: null,
};

function buildCommandStats(
  scoredItems: ScoredItem[],
  leaderboard: LeaderboardRow[],
  userId?: string,
): CommandStats {
  const now = new Date();
  const weekStart = startOfWeek(now);
  const nextWeekStart = addDays(weekStart, 7);

  const validItems = scoredItems.filter(
    (item): item is ScoredItem & { created_at: string } => Boolean(item.created_at),
  );

  const timestamps = validItems.map((item) => new Date(item.created_at));
  const { dayKeys, weekCounts } = buildStreakMaps(timestamps);

  const promptsThisWeek = timestamps.filter(
    (ts) => ts >= weekStart && ts < nextWeekStart,
  ).length;

  const leaderboardIndex = userId
    ? leaderboard.findIndex((row) => row.user_id === userId)
    : -1;

  return {
    dailyStreak: getCurrentDailyStreak(dayKeys),
    weeklyStreak: getCurrentWeeklyStreak(weekCounts),
    promptsThisWeek,
    totalPrompts: validItems.length,
    teamRank: leaderboardIndex >= 0 ? leaderboardIndex + 1 : null,
    teamMemberCount: leaderboard.length || null,
  };
}

export { WEEKLY_GOAL };

export function useCommandStats(userId?: string, orgId?: string) {
  const [stats, setStats] = useState<CommandStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      try {
        const leaderboardPromise = orgId
          ? supabase.rpc("get_org_points_leaderboard", {
              _org_id: orgId,
              _limit: 500,
            })
          : Promise.resolve({ data: [] as LeaderboardRow[], error: null });

        const [scoredItemsRes, leaderboardRes] = await Promise.all([
          supabase
            .from("v_scored_items")
            .select("created_at, score_total_1_100")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(5000),
          leaderboardPromise,
        ]);

        if (cancelled) return;

        if (scoredItemsRes.error)
          console.error("Error fetching scored items:", scoredItemsRes.error);
        if (leaderboardRes.error)
          console.error("Error fetching leaderboard:", leaderboardRes.error);

        setStats(
          buildCommandStats(
            scoredItemsRes.data || [],
            leaderboardRes.data || [],
            userId,
          ),
        );
      } catch (err) {
        console.error("Error fetching command stats:", err);
        if (!cancelled) setStats(EMPTY_STATS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, [userId, orgId]);

  return { stats, loading };
}
